// Minimal IndexedDB-backed queue for offline write requests
import { encryptPayload, decryptPayload } from "./crypto";
import { openOfflineDb, OFFLINE_REQUEST_STORE } from './offlineDb';

const STORE_NAME = OFFLINE_REQUEST_STORE;

function activeContextOwnerKey() {
  const user = localStorage.getItem('userId') || 'unknown-user';
  try {
    const token = localStorage.getItem('velaToken');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
      const context = payload.contextReferenceId || payload.organizationReferenceId || payload.organizationId;
      return `${context || 'unknown-context'}:${user}`;
    }
  } catch {}
  return `unknown-context:${user}`;
}

function withStore(mode, fn) {
  return openOfflineDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, mode);
        const store = tx.objectStore(STORE_NAME);
        const result = fn(store);
        tx.oncomplete = () => resolve(result);
        tx.onerror = () => reject(tx.error);
      })
  );
}

export async function enqueueRequest(item) {
  const id = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const createdAt = Date.now();
  const ownerKey = activeContextOwnerKey();
  const headers = new Headers(item.headers || {});
  headers.delete('Authorization');
  headers.delete('Cookie');
  const encrypted = await encryptPayload({ ...item, headers: Object.fromEntries(headers.entries()) });
  if (ownerKey !== activeContextOwnerKey()) throw new Error('Practice changed while retaining this request.');
  const record = { id, createdAt, ownerKey, encrypted };
  await withStore("readwrite", (store) => store.add(record));
  return id;
}

export async function getAllRequests() {
  const db = await openOfflineDb();
  const raw = await new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);
    const index = store.index("createdAt");
    const req = index.getAll();
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
  const items = [];
  const ownerKey = activeContextOwnerKey();
  for (const rec of raw) {
    // Records from older releases have no owner. They remain available to the
    // explicit legacy-recovery workflow, but can never sync under an arbitrary
    // newly selected practice context.
    if (rec.ownerKey !== ownerKey) continue;
    try {
      if (rec.encrypted) {
        const item = await decryptPayload(rec.encrypted);
        items.push({ id: rec.id, createdAt: rec.createdAt, ...item });
      } else {
        const { id, createdAt, ...rest } = rec;
        items.push({ id, createdAt, ...rest });
      }
    } catch (e) {
      // skip malformed entries
    }
  }
  items.sort((a, b) => a.createdAt - b.createdAt);
  return items;
}

export async function removeRequest(id) {
  await withStore("readwrite", (store) => store.delete(id));
}

export async function getRawRequestRecords({ includeOtherOwners = true } = {}) {
  const db = await openOfflineDb();
  const records = await new Promise((resolve, reject) => {
    const request = db.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
  if (includeOtherOwners) return records;
  const ownerKey = activeContextOwnerKey();
  return records.filter((record) => record.ownerKey === ownerKey);
}
